import { observer } from 'mobx-react-lite';
import React, { useEffect, useState, useRef } from 'react';
import Popup from '../../../../../../../components/common-components/pop-up/Popup';
import { useStore } from '../../../../../../../hooks/useStore';
import { Form, Formik } from 'formik';
import CommonButton from '../../../../../../../components/common-components/button/CommonButton';
import { FormikTextField } from '../../../../../../../components/form-components/common-components/text-field/FormikTextField';
import { FormikDropdown } from '../../../../../../../components/form-components/common-components/dropdown/FormikDropdown';
import * as Yup from 'yup';
import styles from './NewRowManuel.module.css';
import { IHospitalRecordParam } from '../../../../../../../models/hospital/HospitalModel';
import { toast } from 'react-toastify';

const validationSchema = Yup.object({
  color: Yup.string().required('Etiket rengi zorunludur'),
  labelNo: Yup.number()
    .required('Etiket numarası zorunludur')
    .min(0, 'Etiket numarası 0 veya daha büyük olmalıdır'),
});

const tagColorOptions = [
  { value: 'red', label: 'Kırmızı Etiket' },
  { value: 'yellow', label: 'Sarı Etiket' },
  { value: 'green', label: 'Yeşil Etiket' },
];
const NewRowManuel = () => {
  const { popupStore, hospitalFilesStore, authStore } = useStore();
  const [selectedTagColor, setSelectedTagColor] = useState<string>('green');
  const formikRef = useRef<any>(null);

  const getNextLabelForColor = (color: string): number => {
    const colorItem = hospitalFilesStore.tagColors?.find(
      x => x.color?.toLowerCase() === color?.toLowerCase()
    );
    if (colorItem && typeof colorItem.sequence === 'number') {
      return colorItem.sequence;
    }
    return 0;
  };

  useEffect(() => {
    const savedTagColor = localStorage.getItem('selectedTagColor');
    if (savedTagColor) {
      setSelectedTagColor(savedTagColor);
    }
    if (!hospitalFilesStore.tagColors?.length && hospitalFilesStore.selectedTag?.id) {
      hospitalFilesStore.getTagColors();
    }
  }, []);

  useEffect(() => {
    // When tag colors or selected color changes, auto set the next label number
    if (formikRef.current) {
      formikRef.current.setFieldValue('labelNo', getNextLabelForColor(selectedTagColor));
    }
  }, [hospitalFilesStore.tagColors, selectedTagColor]);

  const handleTagColorChange = (color: string) => {
    setSelectedTagColor(color);
    localStorage.setItem('selectedTagColor', color);
  };

  const initialValues = {
    tagColor: selectedTagColor,
    labelNo: getNextLabelForColor(selectedTagColor),
    deviceDefinition: '',
    brand: '',
    model: '',
    serialNumber: '',
    tagNumber: '',
    location: '',
    owner: '',
    notes: '',
    color: selectedTagColor, // Color alanı eklendi
  };

  const handleSubmit = async (values: typeof initialValues) => {
    try {
      if (!hospitalFilesStore.selectedHospital) {
        toast.error('Lütfen bir hastane seçiniz');
        return;
      }

      if (!authStore.user) {
        toast.error('Kullanıcı bilgisi bulunamadı');
        return;
      }

      const newRecord: IHospitalRecordParam = {
        tagId: hospitalFilesStore.selectedTag?.id,
        tagSequence: Number(values.labelNo ?? 0),
        tagColor: selectedTagColor,
        biomedicalDeviceDescription: values.deviceDefinition,
        brand: values.brand,
        model: values.model,
        serialNumber: values.serialNumber,
        identificationNumber: values.tagNumber,
        location: values.location,
        responsiblePerson: values.owner,
        note: values.notes,
        hospitalId: hospitalFilesStore.selectedHospital.id,
        userId: authStore.user.id,
        color: values.color,
      };

      const response = await hospitalFilesStore.newRowManuel(newRecord);
      if (!response.error) {
        hospitalFilesStore.refreshSelectedTag();
        popupStore.setIsNewManuelRowOpen(false);
        toast.success('Kayıt başarıyla eklendi', {
          position: 'top-right',
          autoClose: 3000,
        });
      }
      if (response.error == true) {
        toast.error('Veri eklenirken bir hata meydana geldi. ' + response.message);
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('Kayıt eklenirken bir hata oluştu', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  const handleSuggestionSearch = async (fieldName: string, searchTerm: string) => {
    return hospitalFilesStore.searchFieldSuggestions(fieldName, searchTerm);
  };

  return (
    <Popup
      isOpen={popupStore.isNewManuelRowOpen}
      onClose={() => popupStore.setIsNewManuelRowOpen(false)}
      title="Yeni Veri Girişi"
    >
      <div className={styles.container}>
        <Formik
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize={true}
        >
          {({ isSubmitting, setFieldValue, values }) => (
            <Form className={styles.form}>
              <div className={styles.inputContainer}>
                <FormikDropdown
                  name="color"
                  label="Etiket Rengi"
                  placeholder="Etiket rengi seçiniz"
                  options={tagColorOptions}
                  onChange={(value: string | number) => {
                    if (value) {
                      const v = value as string;
                      handleTagColorChange(v);
                      setFieldValue('color', v);
                      setFieldValue('labelNo', getNextLabelForColor(v));
                    }
                  }}
                />
              </div>
              <div className={styles.inputContainer}>
                <FormikTextField
                  name="labelNo"
                  label="Etiket No"
                  placeholder="Etiket no giriniz"
                  type="number"
                />
              </div>
              <div className={styles.inputContainer}>
                <FormikTextField
                  name="deviceDefinition"
                  label="Biyomedikal Cihaz Tanımı"
                  placeholder="Cihaz tanımını giriniz"
                  fieldToSearch="biomedicalDeviceDescription"
                  onSuggestionSearch={handleSuggestionSearch}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="brand"
                  label="Marka"
                  placeholder="Marka giriniz"
                  fieldToSearch="brand"
                  onSuggestionSearch={handleSuggestionSearch}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="model"
                  label="Model"
                  placeholder="Model giriniz"
                  fieldToSearch="model"
                  onSuggestionSearch={handleSuggestionSearch}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="serialNumber"
                  label="Seri Numarası"
                  placeholder="Seri numarası giriniz"
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="tagNumber"
                  label="Künye Numarası"
                  placeholder="Künye numarası giriniz"
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="location"
                  label="Bulunduğu Yer"
                  placeholder="Bulunduğu yeri giriniz"
                  fieldToSearch="location"
                  onSuggestionSearch={handleSuggestionSearch}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField
                  name="owner"
                  label="Zimmet Sahibi"
                  placeholder="Zimmet sahibini giriniz"
                  fieldToSearch="responsiblePerson"
                  onSuggestionSearch={handleSuggestionSearch}
                />
              </div>

              <div className={styles.inputContainer}>
                <FormikTextField name="notes" label="Not" placeholder="Not giriniz" />
              </div>

              <div className={styles.buttonContainer}>
                <CommonButton
                  type="primary"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                  className={styles.submitButton}
                >
                  Kaydet
                </CommonButton>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Popup>
  );
};

export default observer(NewRowManuel);
